/**
 * Order printable HTML — shared between OrderDetailPage (single-order
 * print buttons) and OrdersPage (bulk multi-select print).
 *
 * The page layout is a 1:1 port of OrderDetailPage.handlePrintOrder so
 * single-order prints stay byte-identical. For multi-order prints,
 * `buildOrdersPrintHTML` concatenates per-order bodies separated by
 * CSS page-breaks so the output is one document with each order on a
 * fresh page.
 */
import { generateBusinessHeader } from './printUtils';
import { generateBarcodeSVGString } from './barcodePrintUtils';

const ORDER_PRINT_STYLES = `
  body {
    font-family: Arial, sans-serif;
    margin: 20px;
    direction: rtl;
  }
  h1, h2 {
    margin-bottom: 10px;
  }
  .header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
    border-bottom: 1px solid #ccc;
    padding-bottom: 10px;
  }
  .section {
    margin-bottom: 20px;
  }
  table {
    width: 100%;
    border-collapse: collapse;
  }
  th, td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: right;
  }
  th {
    background-color: #f2f2f2;
  }
  .total-row {
    font-weight: bold;
  }
  .payment-status {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 4px;
    font-weight: bold;
    margin-right: 5px;
  }
  .item-status {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 0.9em;
  }
  .item-status.supplied { background-color: #e8f5e9; color: #4caf50; }
  .item-status.not-supplied { background-color: #ffebee; color: #f44336; }
  .item-barcode { margin-top: 6px; line-height: 0; }
  .item-barcode svg { display: block; max-width: 220px; height: auto; }
  .footer {
    margin-top: 30px;
    text-align: center;
    font-size: 0.8em;
    color: #666;
  }
  .order-page + .order-page { page-break-before: always; break-before: page; }
  @media print {
    .no-print { display: none; }
    button { display: none; }
  }
`;

const fmtILS = (n) =>
  new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(n || 0);

const STATUS_FALLBACK = [
  { value: 'pending', label: 'בתהליך' },
  { value: 'processing', label: 'בטיפול' },
  { value: 'completed', label: 'הושלם' },
  { value: 'cancelled', label: 'בוטל' },
];

const PAYMENT_METHOD_HE = {
  cash: 'מזומן',
  credit_card: 'כרטיס אשראי',
  bank_transfer: 'העברה בנקאית',
  check: "צ'ק",
};

const statusName = (status, schema) => {
  const opts = schema?.Status?.options || STATUS_FALLBACK;
  return opts.find((o) => o.value === status)?.label || status;
};

/**
 * Build the inner HTML body for one printable order. Doesn't include
 * <html>/<head>/<style>/<body> wrapping — caller is responsible for
 * embedding it in a full HTML doc (single or multi).
 */
export function buildOrderPrintBody(order, options = {}) {
  const {
    businessProfile = {},
    orderLabel = 'הזמנה',
    productLabel = 'מוצר',
    productPluralLabel = 'מוצרים',
    orderFields = [],
    orderFieldsSchema = {},
    orderItemFields = [],
    orderPrintItemFields = [],
    barcodePrintSymbology,
    includeFooter = true,
  } = options;

  const totalsColspan = orderItemFields.includes('Status') ? '5' : '4';

  const itemsRows = (order.items || []).map((item, index) => {
    const printBarcode = orderPrintItemFields.includes('Barcode');
    const barcodeSvg = (printBarcode && item.ProductBarcode)
      ? generateBarcodeSVGString(item.ProductBarcode, {
          width: 1.4, height: 28, fontSize: 9, textMargin: 1, margin: 2,
          preferredSymbology: barcodePrintSymbology,
        })
      : '';
    return `
      <tr>
        <td>${index + 1}</td>
        <td>
          ${item.ProductName}
          ${barcodeSvg ? `<div class="item-barcode">${barcodeSvg}</div>` : ''}
        </td>
        <td>${item.Quantity}</td>
        <td>${fmtILS(item.UnitPrice)}</td>
        <td>${fmtILS(item.Subtotal)}</td>
        ${orderItemFields.includes('Status') ? `
          <td>
            <span class="item-status ${item.Status === 'supplied' ? 'supplied' : 'not-supplied'}">
              ${item.StatusDisplay || (item.Status === 'supplied' ? 'סופק' : 'לא סופק')}
            </span>
          </td>
        ` : ''}
      </tr>
    `;
  }).join('');

  const subtotalRows = order.Subtotal !== order.TotalAmount ? `
    <tr>
      <td colspan="${totalsColspan}" style="text-align: left;"><strong>סה"כ לפני הנחה:</strong></td>
      <td>${fmtILS(order.Subtotal)}</td>
    </tr>
    <tr>
      <td colspan="${totalsColspan}" style="text-align: left;"><strong>הנחה:</strong></td>
      <td>${fmtILS(order.DiscountAmount)}</td>
    </tr>
    <tr>
      <td colspan="${totalsColspan}" style="text-align: left;"><strong>סה"כ אחרי הנחה:</strong></td>
      <td>${fmtILS((order.Subtotal || 0) - (order.DiscountAmount || 0))}</td>
    </tr>
  ` : '';

  const taxIncludedRow = order.IsTaxable && order.IsTaxIncluded && order.TaxAmount > 0 ? `
    <tr>
      <td colspan="${totalsColspan}" style="text-align: left; color: #666; font-size: 0.9em;">מזה מע"מ (${order.TaxRate || 18}%):</td>
      <td style="color: #666; font-size: 0.9em;">${fmtILS(order.TaxAmount)}</td>
    </tr>
  ` : '';

  const taxAddedRow = order.IsTaxable && !order.IsTaxIncluded && order.TaxAmount > 0 ? `
    <tr>
      <td colspan="${totalsColspan}" style="text-align: left;"><strong>מע"מ (${order.TaxRate || 18}%):</strong></td>
      <td>+${fmtILS(order.TaxAmount)}</td>
    </tr>
  ` : '';

  const roundingRows = order.RoundingEnabled && order.RoundingAdjustment !== 0 ? `
    <tr>
      <td colspan="${totalsColspan}" style="text-align: left; color: #666; font-size: 0.9em;">סה"כ לפני עיגול:</td>
      <td style="color: #666; font-size: 0.9em;">${fmtILS((order.TotalAmount || 0) - (order.RoundingAdjustment || 0))}</td>
    </tr>
    <tr>
      <td colspan="${totalsColspan}" style="text-align: left; color: #1976d2; font-size: 0.9em;">עיגול:</td>
      <td style="color: #1976d2; font-size: 0.9em;">${fmtILS(order.RoundingAdjustment)}</td>
    </tr>
  ` : '';

  const paymentsSection = orderFields.includes('PaidAmount') ? `
    <div class="section">
      <h2>תשלומים</h2>
      <p><strong>סכום ששולם:</strong> ${fmtILS(order.PaidAmount || 0)}</p>
      <p><strong>יתרה לתשלום:</strong> ${fmtILS((order.TotalAmount || 0) - (order.PaidAmount || 0))}</p>
      ${order.payments && order.payments.length > 0 ? `
        <table>
          <thead>
            <tr>
              <th>תאריך</th>
              <th>אמצעי תשלום</th>
              <th>פרטים</th>
              <th>סכום</th>
            </tr>
          </thead>
          <tbody>
            ${order.payments.map((payment) => {
              const method = PAYMENT_METHOD_HE[payment.PaymentMethod] || payment.PaymentMethod;
              let details = '';
              if (payment.PaymentMethod === 'check' && payment.CheckNumber) {
                details = `מספר צ'ק: ${payment.CheckNumber}`;
                if (payment.CheckAccountNumber) details += ` | חשבון: ${payment.CheckAccountNumber}`;
              } else if (payment.PaymentMethod === 'credit_card' && payment.CardLastFour) {
                details = `כרטיס: ${payment.CardLastFour}`;
              } else if (payment.PaymentMethod === 'bank_transfer' && payment.BankName) {
                details = `בנק: ${payment.BankName}`;
              }
              return `
                <tr>
                  <td>${new Date(payment.PaymentDate).toLocaleDateString('he-IL')}</td>
                  <td>${method}</td>
                  <td>${details}</td>
                  <td>${fmtILS(payment.Amount)}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      ` : '<p>אין תשלומים</p>'}
    </div>
  ` : '';

  return `
    ${generateBusinessHeader(businessProfile)}
    <div class="header">
      <div>
        <h1>${orderLabel} מס' ${order.OrderNumber}</h1>
        <p>תאריך: ${new Date(order.OrderDate).toLocaleDateString('he-IL')}</p>
        ${orderFields.includes('DueDate') && order.DueDate ? `<p>תאריך אספקה: ${new Date(order.DueDate).toLocaleDateString('he-IL')}</p>` : ''}
      </div>
      <div>
        ${orderFields.includes('Status') ? `<p><strong>סטטוס:</strong> ${statusName(order.Status, orderFieldsSchema)}</p>` : ''}
        ${order.InvoiceNumber ? `<p><strong>מספר חשבונית:</strong> ${order.InvoiceNumber}</p>` : ''}
      </div>
    </div>

    <div class="section">
      <h2>פרטי לקוח</h2>
      <p><strong>שם:</strong> ${order.ClientName}</p>
      <p><strong>מספר לקוח:</strong> ${order.ClientBusinessID}</p>
    </div>

    <div class="section">
      <h2>${productPluralLabel}</h2>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>שם ${productLabel}</th>
            <th>כמות</th>
            <th>מחיר יחידה</th>
            <th>סה"כ</th>
            ${orderItemFields.includes('Status') ? '<th>סטטוס</th>' : ''}
          </tr>
        </thead>
        <tbody>
          ${itemsRows}
          ${subtotalRows}
          ${taxIncludedRow}
          ${taxAddedRow}
          ${roundingRows}
          <tr class="total-row">
            <td colspan="${totalsColspan}" style="text-align: left;"><strong>סה"כ לתשלום:</strong></td>
            <td>${fmtILS(order.TotalAmount)}</td>
          </tr>
        </tbody>
      </table>
    </div>

    ${paymentsSection}

    ${order.Notes ? `
      <div class="section">
        <h2>הערות</h2>
        <p>${order.Notes}</p>
      </div>
    ` : ''}

    ${includeFooter ? `
      <div class="footer">
        <p>הודפס בתאריך: ${new Date().toLocaleDateString('he-IL')}</p>
      </div>
    ` : ''}
  `;
}

/**
 * Wrap one or more order bodies into a full printable HTML document.
 * Each order goes inside `.order-page`; CSS forces a page break between
 * consecutive `.order-page` siblings so each order starts on its own page.
 */
export function buildOrdersPrintHTML(orders, options = {}) {
  const { title } = options;
  const bodies = (orders || []).map((order) => {
    // Each per-order body keeps its own footer — that's the dated stamp the
    // single-order print already shows. Keeps the bundled output consistent
    // with what users see today when printing one order.
    const inner = buildOrderPrintBody(order, options);
    return `<div class="order-page">${inner}</div>`;
  }).join('\n');

  const docTitle = title
    || (orders.length === 1 ? `${options.orderLabel || 'הזמנה'} ${orders[0]?.OrderNumber || ''}` : `${orders.length} ${options.orderPluralLabel || 'הזמנות'}`);

  return `<!DOCTYPE html>
<html dir="rtl">
<head>
  <title>${docTitle}</title>
  <meta charset="utf-8" />
  <style>${ORDER_PRINT_STYLES}</style>
</head>
<body>
${bodies}
</body>
</html>`;
}
